import React from 'react';

import Alert from 'react-bootstrap/Alert';

import { IApiResult } from '../model/api';
import { useStateWithStorage } from '../utils/storage';

export interface IApiContext {
	ready: boolean;
	fetch: (route: string, input: RequestInit, output: <T extends IApiResult>(result: T) => void) => void;
	warn: (message?: string) => React.JSX.Element;
}

const api = process.env.GATSBY_API_URL;

const defaultPayload: RequestInit = {
	method: 'GET', // GET, POST, PUT, DELETE
	cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
	credentials: 'omit', // include, *same-origin, omit
	headers: { 'Content-Type': 'application/json' },
	redirect: 'follow', // manual, *follow, error
	referrer: 'client', // no-referrer, *client
};

const defaultApi: IApiContext = {
	ready: false,
	fetch: () => new Promise((_resolve, reject) => reject()),
	warn: () => <></>
};

export const ApiContext = React.createContext<IApiContext>(defaultApi);

type ApiProviderProps = {
	children: React.ReactNode;
};

export const ApiProvider = (props: ApiProviderProps) => {
	const { children } = props;

	const [ready, setReady] = useStateWithStorage('apiReady', false);

	const providerValue: IApiContext = { ready, fetch: apiFetch, warn: apiWarn };

	return (
		<ApiContext.Provider value={providerValue}>
			{children}
		</ApiContext.Provider>
	);

	async function apiTest(): Promise<boolean> {
		return new Promise(resolve => {
			if (ready) {
				resolve(true);
			}
			else if (api === '') {
				resolve(false);	// API not defined in local environment
			}
			else {
				fetch(`${api}test`)
					.then(response => response.json())
					.then(result => {
						if (result.response === 1) {
							resolve(true);
							setReady(true);
						}
						else {
							resolve(false); // API server likely disabled remotely
						}
					})
					.catch(error => {
						resolve(false);	// Likely network error preventing connection to API
						console.log(error);
					});
			}
		});
	}

	function apiFetch(route: string, input: RequestInit, output?: <T extends IApiResult>(result: T) => void): void {
		apiTest().then(apiReady => {
			if (apiReady) {
				const payload: RequestInit = {...defaultPayload, ...input};
				fetch(`${api}${route}`, payload)
					.then(response => response.json())
					.then(result => output && output(result))
					.catch(error => {
						if (output) output({response: -1, message: error.message});
						setReady(false);
						console.log(error.message);
					});
			}
			else {
				if (output) output({response: -1, message: 'API unavailable'});
			}
		});
	}

	function apiWarn(message: string = 'Please try again later.'): React.JSX.Element {
		if (ready) return <></>;
		apiTest();
		return (
			<Alert variant='warning'>
				The Tower of the Hand API is currently unavailable.
				{` `}
				<span dangerouslySetInnerHTML={{ __html: message }} />
			</Alert>
		);
	}
};
